if (!customElements.get('quick-nav')) {
  class QuickNav extends HTMLElement {
    constructor() {
      super();
      this.hydrateOptions();
      this.childSelect = this.querySelector('custom-select[data-level="2"]');
      this.grandchildSelect = this.querySelector('custom-select[data-level="3"]');
      this.submitButton = this.querySelector('.js-submit');
      this.priceRange = this.querySelector('.js-quick-nav-price');

      this.addEventListener('change', this.handleQuickNavChange);
      this.addEventListener('click', this.handleQuickNavClick);
    }

    /**
     * Builds the menu options from a compact, single-pass Liquid payload. This
     * avoids rendering a large three-level navigation tree multiple times on
     * Shopify's servers while preserving the existing custom-select behavior.
     */
    hydrateOptions() {
      const dataElement = this.querySelector('[data-quick-nav-options]');
      if (!dataElement) return;

      try {
        this.optionsByLevel = JSON.parse(dataElement.textContent);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Unable to load quick navigation options.', error);
        return;
      }

      const firstSelect = this.querySelector('custom-select[data-level="1"]');
      this.hydrateSelect(firstSelect, '1');

      dataElement.remove();
    }

    /**
     * Rebuilds one select with only the options relevant to its current parent.
     * Keeping later levels out of the initial DOM avoids layout and style work
     * for hundreds of hidden menu items.
     * @param {HTMLElement} selectElement - Select to populate.
     * @param {string} level - Navigation level to populate.
     * @param {string} [parentValue] - Selected value from the preceding level.
     */
    hydrateSelect(selectElement, level, parentValue) {
      if (!selectElement || !this.optionsByLevel) return;

      const listbox = selectElement.querySelector('.quick-nav__listbox');
      const defaultOption = selectElement.querySelector('.quick-nav__default-option');
      const optionIdPrefix = defaultOption.id.replace(/-opt-default$/, '-opt-');
      const levelOptions = this.optionsByLevel[level] || [];
      const options = level === '1'
        ? levelOptions
        : levelOptions.filter((option) => parentValue && option.parentHandle === parentValue);

      listbox
        .querySelectorAll('.custom-select__option:not(.quick-nav__default-option)')
        .forEach((option) => option.remove());

      options.forEach((option, index) => {
        listbox.appendChild(this.createOption(option, selectElement, `${optionIdPrefix}${index}`));
      });

      listbox.dataset.linkCount = options.length < 5 ? options.length : 'loads';

      // custom-select caches its options when it is upgraded. Refresh those
      // references after rebuilding a later level so mouse and keyboard input
      // continue to behave exactly like the server-rendered version.
      if ('options' in selectElement) {
        defaultOption.setAttribute('aria-selected', 'true');
        selectElement.options = selectElement.querySelectorAll('.custom-select__option');
        selectElement.selectedOption = defaultOption;
        selectElement.focusedOption = null;
        selectElement.swatches = Array.from(selectElement.options).some(
          (option) => 'swatch' in option.dataset
        );
      }
    }

    /**
     * Creates one option using the markup expected by custom-select.js.
     * @param {object} option - Serialized navigation link.
     * @param {HTMLElement} selectElement - Select that owns the option.
     * @param {string} id - Unique option id.
     * @returns {HTMLLIElement}
     */
    createOption(option, selectElement, id) {
      const display = selectElement.dataset.display;
      const boxed = selectElement.dataset.boxed === 'true';
      const listItem = document.createElement('li');
      listItem.className = `custom-select__option quick-nav__option text-start flex items-center js-option${display.includes('image') && option.image ? ' image-blend' : ''}`;
      listItem.id = id;
      listItem.setAttribute('role', 'option');
      listItem.dataset.value = option.value;
      listItem.dataset.url = option.url;
      if (option.parentHandle) listItem.dataset.parentHandle = option.parentHandle;
      if (option.isCollection) listItem.dataset.isCollection = 'true';
      if (display === 'image') listItem.setAttribute('aria-label', option.title);
      if (selectElement.dataset.level !== '1') listItem.style.display = 'none';

      const content = document.createElement('span');
      content.className = `quick-nav__option-content flex items-center pointer-events-none${display === 'image' ? ' justify-center' : ''}`;

      if (display.includes('image') && option.image) {
        content.appendChild(this.createOptionImage(option, boxed, false));
        if (boxed) content.appendChild(this.createOptionImage(option, true, true));
      }

      const label = document.createElement('span');
      if (display === 'image') label.className = 'md:hidden';
      label.textContent = option.title;
      content.appendChild(label);
      listItem.appendChild(content);
      return listItem;
    }

    /**
     * Creates the small or boxed image used by an option.
     * @param {object} option - Serialized navigation link.
     * @param {boolean} boxed - Whether the select uses boxed desktop options.
     * @param {boolean} desktop - Whether this is the desktop image.
     * @returns {HTMLDivElement}
     */
    createOptionImage(option, boxed, desktop) {
      const wrapper = document.createElement('div');
      if (boxed && !desktop) wrapper.className = 'md:hidden';

      const media = document.createElement('div');
      media.className = `media relative quick-nav__img-thumb${desktop ? ' hidden md:block' : ''}`;
      media.style.paddingTop = `${100 / (parseFloat(this.dataset.imageRatio) || 1)}%`;

      const image = document.createElement('img');
      image.src = option.image;
      image.className = `img-fit${this.dataset.imageFit === 'contain' ? ' img-fit--contain' : ''}`;
      image.loading = 'lazy';
      image.width = desktop ? 200 : 80;
      image.alt = '';
      media.appendChild(image);
      wrapper.appendChild(media);
      return wrapper;
    }

    /**
     * Pauses an auto-playing slideshow
     */
    handleQuickNavClick() {
      const slideshowElem = this.closest('slide-show');
      if (slideshowElem && slideshowElem.dataset.autoplay === 'true') {
        const autoplayBtn = slideshowElem.querySelector('.autoplay-btn');
        if (autoplayBtn) autoplayBtn.click();
      }
    }

    /**
     * Handles when anything in the quick nav is changed (one of the selects)
     * @param {object} evt - Event object
     */
    handleQuickNavChange(evt) {
      if (evt.target.matches('custom-select') && evt.detail.selectedValue) {
        const selectedOption = evt.target.querySelector('[aria-selected="true"]');
        this.url = selectedOption.dataset.url;
        this.updateSubmitButton(selectedOption.dataset.url);

        if (selectedOption.dataset.isCollection) {
          this.parseCollectionInfo(selectedOption.dataset.url);
        } else {
          this.unparseCollectionInfo();
        }

        if (evt.target.dataset.level === '1' && this.childSelect) {
          this.hydrateSelect(this.childSelect, '2', evt.detail.selectedValue);
          this.hydrateSelect(this.grandchildSelect, '3');
          QuickNav.prepareSelect(this.childSelect, evt.detail.selectedValue);
          QuickNav.resetSelect(this.grandchildSelect, true);
        } else if (evt.target.dataset.level === '2' && this.grandchildSelect) {
          this.hydrateSelect(this.grandchildSelect, '3', evt.detail.selectedValue);
          QuickNav.prepareSelect(this.grandchildSelect, evt.detail.selectedValue);
        }
      } else if (evt.target.closest('.price-range')) {
        const priceRangeMin = this.priceRange.querySelector("[name='filter.v.price.gte']").value;
        const priceRangeMax = this.priceRange.querySelector("[name='filter.v.price.lte']").value;

        let url = this.url; // eslint-disable-line
        if (priceRangeMin) {
          url = url.includes('?') ? `${url}&` : `${url}?`;
          url += `filter.v.price.gte=${priceRangeMin}`;
        }
        if (priceRangeMax) {
          url = url.includes('?') ? `${url}&` : `${url}?`;
          url += `filter.v.price.lte=${priceRangeMax}`;
        }
        this.updateSubmitButton(url);
        this.parseCollectionInfo(url);
      } else {
        QuickNav.resetSelect(evt.target, false);
      }
    }

    /**
     * Dynamically load the price range slider and updates the number of results
     * @param {string} url - Collection url to fetch
     * @returns {Promise<void>}
     */
    async parseCollectionInfo(url) {
      this.submitButton.ariaDisabled = 'true';
      this.submitButton.classList.add('is-loading');
      let preventButtonEnable = false;

      // Keep the destination URL unchanged, but calculate the quick-nav count
      // from Shopify's filtered collection view so it only includes products
      // with at least one available variant.
      const countUrl = new URL(url, window.location.origin);
      countUrl.searchParams.set('filter.v.availability', '1');

      // Fetch collection page
      const response = await fetch(countUrl.toString());
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const productsContainer = tmpl.content.querySelector('[data-num-results]');
        if (productsContainer) {
          const numResults = parseInt(productsContainer.dataset.numResults, 10);
          if (numResults === 0) {
            this.submitButton.textContent = theme.strings.quickNav.show_products_none;
            preventButtonEnable = true;
          } else if (numResults === 1) {
            this.submitButton.textContent = theme.strings.quickNav.button_one.replace(
              '[quantity]',
              numResults
            );
          } else {
            this.submitButton.textContent = theme.strings.quickNav.button_other.replace(
              '[quantity]',
              numResults
            );
          }
        } else {
          // eslint-disable-next-line
          console.warn(
            'Enterprise Theme: An element with [data-num-results] could not be found on the product page.',
            url
          );
        }

        if (this.dataset.showPrice) {
          const priceRangeElem = tmpl.content.querySelector('price-range');
          if (priceRangeElem) {
            this.priceRange.innerHTML = priceRangeElem.outerHTML;
            const maxHeight = this.priceRange.querySelector('.price-range').clientHeight;
            this.priceRange.style.maxHeight = `${parseInt(maxHeight, 10) + 1}px`;
          } else {
            // eslint-disable-next-line
            console.warn(
              'Enterprise Theme: No price ranger slider could be found for collection.',
              url
            );
          }
        }
      }

      if (!preventButtonEnable) this.submitButton.removeAttribute('aria-disabled');
      this.submitButton.classList.remove('is-loading');
    }

    /**
     * Hides the price range slider
     */
    unparseCollectionInfo() {
      if (this.priceRange) this.priceRange.style.maxHeight = '0px';
      this.submitButton.textContent = theme.strings.quickNav.button_standard;
    }

    /**
     * Gets a select ready to be opened (hides irrelevant options, etc)
     * @param {object} selectElem - The select to be prepared
     * @param {string} value - The handle of the parent dropdown to filter available options by
     */
    static prepareSelect(selectElem, value) {
      let hasOptions = false;
      let counter = 0;

      // Show only the relevant options
      selectElem
        .querySelectorAll('.custom-select__option:not(.quick-nav__default-option)')
        .forEach((childLink) => {
          if (childLink.dataset.parentHandle === value) {
            hasOptions = true;
            childLink.style.display = '';
            counter += 1;
          } else {
            childLink.style.display = 'none';
          }
        });

      const listbox = selectElem.querySelector('.quick-nav__listbox');
      listbox.setAttribute('data-link-count', counter < 5 ? counter : 'loads');

      QuickNav.resetSelect(selectElem, !hasOptions);

      if (hasOptions) {
        selectElem.querySelector('.custom-select__btn').removeAttribute('disabled');
      }
    }

    /**
     * Updates the URL on the search button
     * @param {string} url - The new URL
     */
    updateSubmitButton(url) {
      // Update the url situation
      if (url && url.length > 0 && url !== '#') {
        this.submitButton.href = url;
        this.submitButton.removeAttribute('aria-disabled');
      } else {
        this.submitButton.href = '#';
        this.submitButton.setAttribute('aria-disabled', 'true');
      }
    }

    /**
     * Resets a select back to its default state
     * @param {object} selectElem - The select element to reset
     * @param {boolean} disable - Whether the select should be disabled or not
     */
    static resetSelect(selectElem, disable) {
      if (selectElem) {
        const listbox = selectElem.querySelector('.custom-select__listbox');
        listbox.removeAttribute('aria-activedescendant');

        const selectedOption = selectElem.querySelector('[aria-selected="true"]');
        if (selectedOption) {
          selectedOption.setAttribute('aria-selected', 'false');
        }

        const label = selectElem.querySelector('.custom-select__btn span');
        label.textContent = label.dataset.defaultText;
        if (disable) {
          selectElem.querySelector('.custom-select__btn').setAttribute('disabled', 'disabled');
        }
      }
    }
  }

  customElements.define('quick-nav', QuickNav);
}
